import { useCallback, useEffect, useRef } from "react";
import { getIsAuthenticated } from "@/src/auth/store/auth-selectors";
import { env } from "@/src/common/constants/env";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { baseApi } from "@/src/common/services/base-api";
import { getAccessToken } from "@/src/common/services/http.service";
import { clearPresence, markUserOffline, markUserOnline, setPresenceUsers } from "../store/presence-slice";

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

type PresenceMessage = { type: "presence"; users: string[] } | { type: "join"; userId: string } | { type: "leave"; userId: string };

const parsePresenceMessage = (raw: unknown): PresenceMessage | null => {
	if (typeof raw !== "object" || raw === null) return null;
	const obj = raw as Record<string, unknown>;
	if (obj.type === "presence" && Array.isArray(obj.users) && obj.users.every((u) => typeof u === "string")) {
		return { type: "presence", users: obj.users as string[] };
	}
	if ((obj.type === "join" || obj.type === "leave") && typeof obj.userId === "string") {
		return { type: obj.type, userId: obj.userId };
	}
	return null;
};

const buildWsUrl = (communityId: string, token: string): string => {
	const base = env.SERVER_URL.replace(/^http/, "ws");
	return `${base}/presence/${communityId}?token=${encodeURIComponent(token)}`;
};

export function usePresence(communityId: string | null) {
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const wsRef = useRef<WebSocket | null>(null);
	const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const attemptRef = useRef(0);
	const unmountedRef = useRef(false);

	const connect = useCallback(() => {
		if (!communityId || !isAuthenticated || unmountedRef.current) return;

		const token = getAccessToken();
		if (!token) return;

		const scheduleInvalidate = () => {
			if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current);
			invalidateTimerRef.current = setTimeout(() => {
				dispatch(baseApi.util.invalidateTags([{ type: "Members", id: "active" }]));
			}, 400);
		};

		const ws = new WebSocket(buildWsUrl(communityId, token));
		wsRef.current = ws;

		ws.onopen = () => {
			attemptRef.current = 0;
		};

		ws.onmessage = (event: MessageEvent<string>) => {
			try {
				const msg = parsePresenceMessage(JSON.parse(event.data));
				if (!msg) return;
				if (msg.type === "presence") {
					dispatch(setPresenceUsers(msg.users));
					scheduleInvalidate();
				} else if (msg.type === "join") {
					dispatch(markUserOnline(msg.userId));
					scheduleInvalidate();
				} else if (msg.type === "leave") {
					dispatch(markUserOffline(msg.userId));
					scheduleInvalidate();
				}
			} catch {}
		};

		ws.onclose = () => {
			if (unmountedRef.current) return;
			attemptRef.current += 1;
			const backoff = Math.min(MIN_BACKOFF_MS * 2 ** (attemptRef.current - 1), MAX_BACKOFF_MS);
			reconnectTimerRef.current = setTimeout(connect, backoff);
		};

		ws.onerror = () => {
			ws.close();
		};
	}, [communityId, isAuthenticated, dispatch]);

	useEffect(() => {
		unmountedRef.current = false;

		if (communityId && isAuthenticated) {
			connect();
		}

		return () => {
			unmountedRef.current = true;
			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
			}
			if (invalidateTimerRef.current) {
				clearTimeout(invalidateTimerRef.current);
			}
			if (wsRef.current) {
				wsRef.current.close();
			}
			dispatch(clearPresence());
		};
	}, [communityId, isAuthenticated, connect, dispatch]);
}
